import {
  ALL_OPTION_VALUE,
  applyRemoveOption,
  applySelectOption,
  buildSelectedValues,
  ensureDefaultSelection,
} from '@/utils/filterSelectors';
import {
  createToolbarAllOption,
  extractToolbarDateRange,
} from '@/composables/shared/useFilterToolbarShared';
import { normalizeCatalogOptions, normalizeCurrencyOptions } from '@/utils/catalogOptions';
import reportsSalesService from '@/services/reportsSales';
import {
  REPORTS_SORT_DIRECTION_NONE,
  buildReportsSortDirectionOptions,
  isReportsEventOutsideElement,
  loadReportsToolbarSelectorContext,
  normalizeReportsSortDirection,
  resolveReportsSortButtonIcon,
  resolveReportsSortButtonTitle,
} from './useReportsFilterToolbarHelpers';

export const createReportsFiltersToolbarState = () => {
  return {
    showFilters: false,
    showDateInput: false,
    dateRangeValue: null,
    isToolbarBootstrapped: false,
    initialDatePresetKey: 'this_year',
    options: {},
    filters: {},
    selectorOptionsSource: {},
    selectorDefinitions: [],
    compositeFilters: {
      exchange_rate: 1,
    },
    showSortMenu: false,
    sortDirection: REPORTS_SORT_DIRECTION_NONE,
  };
};

export const reportsFiltersToolbarComputed = {
  quickFilters() {
    return this.filterConfigsByGroup('top');
  },
  firstRowFilterConfigs() {
    return this.filterConfigsByGroup('expanded_row_1');
  },
  secondRowFilterConfigs() {
    return this.filterConfigsByGroup('expanded_row_2');
  },
  thirdRowFilterConfigs() {
    return this.filterConfigsByGroup('expanded_row_3');
  },
  dateRangePresets() {
    return Array.isArray(this.filterToolbarText?.dateRangePresets)
      ? this.filterToolbarText.dateRangePresets
      : [];
  },
  sortDirectionOptions() {
    return buildReportsSortDirectionOptions(this.filterToolbarText?.sort || {});
  },
  isSortActive() {
    return this.sortDirection !== REPORTS_SORT_DIRECTION_NONE;
  },
  sortButtonIconClass() {
    return resolveReportsSortButtonIcon(this.sortDirection);
  },
  sortButtonTitle() {
    return resolveReportsSortButtonTitle({
      sortDirection: this.sortDirection,
      options: this.sortDirectionOptions,
      prefix: this.filterToolbarText?.sort?.buttonPrefix,
    });
  },
};

export const reportsFiltersToolbarMethods = {
  toggleSortMenu() {
    this.showSortMenu = !this.showSortMenu;
  },
  closeSortMenu() {
    this.showSortMenu = false;
  },
  handleSortMenuOutsideClick(event) {
    if (!this.showSortMenu) {
      return;
    }

    const sortMenu = this.$refs.sortMenu;
    if (isReportsEventOutsideElement(event, sortMenu)) {
      this.closeSortMenu();
    }
  },
  selectSortDirection(value) {
    this.sortDirection = normalizeReportsSortDirection(value);
    this.closeSortMenu();
    this.emitFilters('sort');
  },
  async handleReportScopeChange() {
    await this.loadSelectorOptions();
    this.emitFilters('scope-change');
  },
  definitionByKey(key) {
    const normalizedKey = String(key || '').trim();
    return this.selectorDefinitions.find((definition) => definition.key === normalizedKey) || null;
  },
  filterConfigsByGroup(groupCode) {
    const normalizedGroupCode = String(groupCode || '').trim();
    return this.selectorDefinitions
      .filter((definition) => definition.group === normalizedGroupCode)
      .map((definition) => ({
        key: definition.key,
        label: definition.name,
        placeholder: definition.name,
      }));
  },
  createAllOptionForKey(key) {
    const definition = this.definitionByKey(key);
    const allLabels = {
      [String(key || '').trim()]: `${String(definition?.name || this.filterToolbarText.defaultPrefix).trim()}: ${this.filterToolbarText.allLabel}`,
    };

    return createToolbarAllOption(allLabels, String(key || '').trim());
  },
  isSingleFilter(key) {
    const definition = this.definitionByKey(key);
    if (!definition) {
      return false;
    }

    if (definition.individual === 0 || definition.individual === 1) {
      return definition.individual === 1;
    }

    return definition.multiple === 0;
  },
  isLeftAlignedSingleFilter(key) {
    return [
      'invoiced',
      'receiptType',
      'receipt_type',
      'displayCurrency',
      'display_currency',
      'comprobante',
      'show_in',
    ].includes(String(key || '').trim());
  },
  panelSingleSelectionLabel(key, option) {
    const selectedOption = option && typeof option === 'object'
      ? option
      : (Array.isArray(this.filters?.[key]) ? this.filters[key][0] : null);

    return String(selectedOption?.label || '').trim();
  },
  resolveSingleSelection(key, selected, availableOptions = []) {
    const current = (Array.isArray(selected) ? selected : []).filter((option) => {
      return option && String(option?.value || '').trim() !== '';
    });

    if (current.length > 0) {
      return [current[current.length - 1]];
    }

    const definition = this.definitionByKey(key);
    const defaultValue = String(definition?.defaultValue || '').trim();

    if (defaultValue !== '') {
      const defaultOption = (Array.isArray(availableOptions) ? availableOptions : []).find((option) => {
        return String(option?.value || '').trim() === defaultValue;
      });

      if (defaultOption) {
        return [defaultOption];
      }
    }

    const firstOption = (Array.isArray(availableOptions) ? availableOptions : [])[0];
    return firstOption ? [firstOption] : [];
  },
  normalizeSelectionByKey(key, selected, allOption) {
    const current = Array.isArray(selected) ? selected : [];

    if (this.isSingleFilter(key)) {
      return this.resolveSingleSelection(key, current, this.options?.[key] || []);
    }

    return ensureDefaultSelection(current, allOption);
  },
  selectedLabelWordCount(values) {
    const selected = Array.isArray(values) ? values : [];
    const label = String(selected[0]?.label || '').trim();
    if (label === '') {
      return 0;
    }

    return label.split(/\s+/).filter(Boolean).length;
  },
  isCustomerFilter(key) {
    return ['customers', 'customer'].includes(String(key || '').trim());
  },
  isPeriodOrValueFilter(key) {
    return ['period', 'periods', 'value', 'values'].includes(String(key || '').trim());
  },
  isSalesFilter(key) {
    return String(key || '').trim() === 'sales';
  },
  syncSalesOptionsBySelectedCustomers() {
    const salesDefinition = (Array.isArray(this.selectorDefinitions) ? this.selectorDefinitions : [])
      .find((definition) => this.isSalesFilter(definition?.key));

    const salesKey = String(salesDefinition?.key || '').trim();
    if (salesKey === '') {
      return;
    }

    const rawSalesOptions = Array.isArray(this.selectorOptionsSource?.[salesKey])
      ? this.selectorOptionsSource[salesKey]
      : [];

    this.syncKeyOptions(salesKey, rawSalesOptions);
  },
  isProblematicPanelSingleSelector(key) {
    return [
      'invoiced',
      'receiptType',
      'receipt_type',
      'comprobante',
      'displayCurrency',
      'display_currency',
      'show_in',
    ].includes(String(key || '').trim());
  },
  shouldClosePanelOnSelect(key) {
    return this.isSingleFilter(key);
  },
  shouldPreservePanelSearch(key) {
    return this.isProblematicPanelSingleSelector(key) ? false : true;
  },
  isPanelSelectorSearchable(key) {
    return this.isProblematicPanelSingleSelector(key) ? false : true;
  },
  isDisplayCurrencyFilter(key) {
    return [
      'displayCurrency',
      'display_currency',
      'show_in',
    ].includes(String(key || '').trim());
  },
  selectedSingleFilterValue(key) {
    const selected = Array.isArray(this.filters?.[key]) ? this.filters[key] : [];
    return String(selected[0]?.value || '').trim();
  },
  shouldShowCompositeExchangeRateInput(key) {
    if (!this.isDisplayCurrencyFilter(key)) {
      return false;
    }

    const selectedCurrency = this.selectedSingleFilterValue(key).toLowerCase();
    return selectedCurrency !== '' && selectedCurrency !== 'mxn';
  },
  compositeExchangeRateInputValue(key) {
    if (!this.shouldShowCompositeExchangeRateInput(key)) {
      return '';
    }

    const parsed = Number(this.compositeFilters?.exchange_rate || 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : '';
  },
  setCompositeExchangeRateValue(rawValue) {
    const parsed = Number(rawValue || 0);
    const nextValue = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;

    this.compositeFilters = {
      ...(this.compositeFilters || {}),
      exchange_rate: nextValue,
    };
  },
  onCompositeExchangeRateInput(key, rawValue) {
    if (!this.isDisplayCurrencyFilter(key)) {
      return;
    }

    const normalizedRaw = String(rawValue ?? '').replace(',', '.').trim();
    if (normalizedRaw === '') {
      this.setCompositeExchangeRateValue(0);
      return;
    }

    this.setCompositeExchangeRateValue(normalizedRaw);
  },
  onCompositeExchangeRateBlur(key) {
    if (!this.isDisplayCurrencyFilter(key)) {
      return;
    }

    if (!this.shouldShowCompositeExchangeRateInput(key)) {
      this.setCompositeExchangeRateValue(1);
      return;
    }

    const parsed = Number(this.compositeFilters?.exchange_rate || 0);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      this.setCompositeExchangeRateValue(1);
    }
  },
  syncDisplayCurrencyComposite(key) {
    if (!this.isDisplayCurrencyFilter(key)) {
      return;
    }

    if (!this.shouldShowCompositeExchangeRateInput(key)) {
      this.setCompositeExchangeRateValue(1);
      return;
    }

    const parsed = Number(this.compositeFilters?.exchange_rate || 0);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      this.setCompositeExchangeRateValue(1);
    }
  },
  buildCompositeFiltersPayload() {
    const displayCurrencyKey = [
      'displayCurrency',
      'display_currency',
      'show_in',
    ].find((key) => Array.isArray(this.filters?.[key]));

    if (!displayCurrencyKey || !this.shouldShowCompositeExchangeRateInput(displayCurrencyKey)) {
      return {};
    }

    const exchangeRate = Number(this.compositeFilters?.exchange_rate || 0);
    if (!Number.isFinite(exchangeRate) || exchangeRate <= 0) {
      return {};
    }

    return {
      exchange_rate: exchangeRate,
    };
  },
  syncKeyOptions(key, rawList) {
    const normalizedKey = String(key || '').trim();
    let sourceList = Array.isArray(rawList) ? rawList : [];

    if (this.isSalesFilter(normalizedKey)) {
      const selectedCustomerIds = this.selectedValues('customers').map((value) => String(value || '').trim());

      if (selectedCustomerIds.length > 0) {
        const selectedCustomerIdSet = new Set(selectedCustomerIds);

        sourceList = sourceList.filter((item) => {
          const customerId = String(
            item?.customer_id
            ?? item?.meta?.customer_id
            ?? ''
          ).trim();

          return customerId !== '' && selectedCustomerIdSet.has(customerId);
        });
      }
    }

    const normalized = normalizedKey === 'currency'
      ? normalizeCurrencyOptions(sourceList, { useCodeInLabel: false })
      : normalizeCatalogOptions(sourceList, { useCodeInLabel: false });
    const seenValues = new Set();
    const uniqueOptions = normalized.filter((item) => {
      const value = String(item?.value || '').trim();
      if (value === '' || seenValues.has(value)) {
        return false;
      }

      seenValues.add(value);
      return true;
    });

    if (this.isSingleFilter(key)) {
      const currentSelection = Array.isArray(this.filters?.[key]) ? this.filters[key] : [];
      const allowedValues = new Set(uniqueOptions.map((item) => String(item?.value || '').trim()));
      const validSelection = currentSelection.filter((item) => {
        return allowedValues.has(String(item?.value || '').trim());
      });

      this.options = {
        ...this.options,
        [key]: uniqueOptions,
      };

      this.filters = {
        ...this.filters,
        [key]: this.resolveSingleSelection(key, validSelection, uniqueOptions),
      };

      this.syncDisplayCurrencyComposite(key);

      return;
    }

    const allOption = this.createAllOptionForKey(key);

    const allowedValues = new Set([allOption.value, ...uniqueOptions.map((item) => item.value)]);
    const currentSelection = Array.isArray(this.filters?.[key]) ? this.filters[key] : [];
    const validSelection = currentSelection.filter((item) => allowedValues.has(String(item?.value || '')));

    this.options = {
      ...this.options,
      [key]: [allOption, ...uniqueOptions],
    };

    this.filters = {
      ...this.filters,
      [key]: this.normalizeSelectionByKey(key, validSelection, allOption),
    };

    this.syncDisplayCurrencyComposite(key);
  },
  resetAllOptions() {
    this.options = {};
    this.filters = {};
    this.selectorDefinitions = [];
    this.compositeFilters = {
      exchange_rate: 1,
    };
    this.selectorOptionsSource = {};
  },
  async fetchSelectors(payloadParams) {
    if (typeof this.selectorsFetcher === 'function') {
      return this.selectorsFetcher(payloadParams, { skipLoading: true });
    }

    return reportsSalesService.selectors(payloadParams, { skipLoading: true });
  },
  async loadSelectorOptions() {
    const { definitions, optionsByDefinition } = await loadReportsToolbarSelectorContext({
      fetchSelectors: (payloadParams) => this.fetchSelectors(payloadParams),
      reportKey: this.reportKey,
      reportTab: this.reportTab,
      requestParams: this.requestParams,
      localSelectorDefinitions: this.localSelectorDefinitions,
      localSelectorOptions: this.localSelectorOptions,
    });

    this.selectorDefinitions = definitions;

    if (definitions.length === 0) {
      this.resetAllOptions();
      return;
    }

    const nextOptions = {};
    const nextFilters = {};
    const nextOptionsSource = {};
    definitions.forEach((definition) => {
      nextOptions[definition.key] = [];
      nextFilters[definition.key] = [];
      nextOptionsSource[definition.key] = [];
    });

    this.options = nextOptions;
    this.filters = nextFilters;
    this.selectorOptionsSource = nextOptionsSource;

    optionsByDefinition.forEach(({ definition, rawOptions }) => {
      this.selectorOptionsSource = {
        ...this.selectorOptionsSource,
        [definition.key]: Array.isArray(rawOptions) ? rawOptions : [],
      };

      this.syncKeyOptions(definition.key, rawOptions);
    });

    this.syncSalesOptionsBySelectedCustomers();
  },
  toggleFilters() {
    this.showFilters = !this.showFilters;
    this.showDateInput = this.showFilters;
    this.closeSortMenu();
  },
  handleFiltersAfterLeave() {
    this.showDateInput = false;
  },
  async onSelect(key, option) {
    if (this.isSingleFilter(key)) {
      this.filters = {
        ...this.filters,
        [key]: this.resolveSingleSelection(key, [option].filter(Boolean), this.options?.[key] || []),
      };

      this.syncDisplayCurrencyComposite(key);
      if (this.isCustomerFilter(key)) {
        this.syncSalesOptionsBySelectedCustomers();
      }

      this.emitFilters('select');
      return;
    }

    const allOption = this.options[key]?.[0] || this.createAllOptionForKey(key);

    this.filters[key] = applySelectOption(this.filters[key], option, allOption);

    this.syncDisplayCurrencyComposite(key);
    if (this.isCustomerFilter(key)) {
      this.syncSalesOptionsBySelectedCustomers();
    }

    this.emitFilters('select');
  },
  async onRemove(key) {
    if (this.isSingleFilter(key)) {
      this.filters = {
        ...this.filters,
        [key]: this.resolveSingleSelection(key, [], this.options?.[key] || []),
      };

      this.syncDisplayCurrencyComposite(key);
      if (this.isCustomerFilter(key)) {
        this.syncSalesOptionsBySelectedCustomers();
      }

      this.emitFilters('remove');
      return;
    }

    const allOption = this.options[key]?.[0] || this.createAllOptionForKey(key);
    this.filters[key] = this.normalizeSelectionByKey(
      key,
      applyRemoveOption(this.filters[key], allOption),
      allOption
    );

    this.syncDisplayCurrencyComposite(key);
    if (this.isCustomerFilter(key)) {
      this.syncSalesOptionsBySelectedCustomers();
    }

    this.emitFilters('remove');
  },
  selectionLabel(key, values) {
    const selected = Array.isArray(values) ? values : [];

    if (this.isSingleFilter(key)) {
      const selectedLabel = String(selected[0]?.label || '').trim();
      return `${this.selectorPrefix(key, selected)}: ${selectedLabel}`.trim();
    }

    const allLabel = this.createAllOptionForKey(key)?.label || this.filterToolbarText.allLabel;

    if (selected.length === 0 || selected.some((option) => option?.value === ALL_OPTION_VALUE)) {
      return allLabel;
    }

    if (selected.length === 1) {
      return `${this.selectorPrefix(key)}: ${selected[0]?.label || this.filterToolbarText.allLabel}`;
    }

    return `${this.selectorPrefix(key)}: ${selected.length} ${this.filterToolbarText.selectedLabelSuffix}`;
  },
  singleSelectionLabel(key, option) {
    const selectedOption = option && typeof option === 'object'
      ? option
      : (Array.isArray(this.filters?.[key]) ? this.filters[key][0] : null);
    const selectedLabel = String(selectedOption?.label || '').trim();

    return `${this.selectorPrefix(key, selectedOption ? [selectedOption] : [])}: ${selectedLabel}`.trim();
  },
  buildFilterSelectionLabel(key, values) {
    const selected = Array.isArray(values) ? values : [];

    if (this.isSingleFilter(key)) {
      return String(selected[0]?.label || '').trim();
    }

    if (selected.length === 0 || selected.some((option) => option?.value === ALL_OPTION_VALUE)) {
      return this.filterToolbarText.allLabel;
    }

    if (selected.length === 1) {
      return selected[0]?.label || this.filterToolbarText.allLabel;
    }

    return `${selected.length} ${this.filterToolbarText.selectedLabelSuffix}`;
  },
  selectorPrefix(key, values = []) {
    const normalizedKey = String(key || '').trim();

    if (normalizedKey === 'columns') {
      return this.selectedLabelWordCount(values) > 1
        ? this.filterToolbarText.columnPlural
        : this.filterToolbarText.columnSingular;
    }

    return String(this.definitionByKey(normalizedKey)?.name || this.filterToolbarText.defaultPrefix).trim();
  },
  selectedValues(key) {
    return buildSelectedValues(this.filters[key]);
  },
  extractDateRange() {
    return extractToolbarDateRange(this.dateRangeValue, {
      startKey: 'date_start',
      endKey: 'date_end',
    });
  },
  emitFilters(trigger = 'manual') {
    const dateRange = this.extractDateRange();
    const dynamicSelections = {};
    const compositeSelections = this.buildCompositeFiltersPayload();

    (Array.isArray(this.selectorDefinitions) ? this.selectorDefinitions : []).forEach((definition) => {
      const key = String(definition?.key || '').trim();
      const code = String(definition?.code || '').trim();
      const selected = this.selectedValues(key);

      if (code !== '') {
        dynamicSelections[code] = selected;
      }
    });

    const payload = {
      ...dateRange,
      ...dynamicSelections,
      ...compositeSelections,
      sort_direction: this.sortDirection,
    };

    this.$emit('filters-change', payload, { trigger });
    return payload;
  },
  emitApply() {
    this.$emit('apply', this.emitFilters('apply'));
  },
};
